/**
 * Ambientes renomeados na planilha: aviso + painel para religar ao nome novo
 * o que o Mapa guardou com o nome velho (alocação, tarefas, peças, o
 * histórico de comentários das páginas do Legado, o projetista do executivo
 * e a carga na marcenaria da Capacidade da Fábrica).
 *
 * Só aparece para quem tem `gerir` e só quando há órfão. Sem isto, renomear
 * "BANHO" para "BANHO SERVIÇO" tirava o ambiente da planta e o novo nascia
 * sem posto, sem prazo e sem história.
 */
import React, { useMemo, useState } from "react";
import styled from "styled-components";

import AsyncButton from "../../components/AsyncButton";
import SlideOverPanel from "../../components/SlideOverPanel";
import useContagemComentarios, { recarregarContagemComentarios } from "../../hooks/useContagemComentarios";
import useExcecoesCapacidade, {
  PERMS_LER_EXCECAO_PARA_RELIGAR,
  recarregarExcecoesCapacidade,
} from "../../hooks/useExcecoesCapacidade";
import useProjetistasExecutivo, { recarregarProjetistasExecutivo } from "../../hooks/useProjetistasExecutivo";
import { religarAmbiente } from "../../services/mapaProducao";
import { plural } from "../../utils/dinheiro";
import { ambientesOrfaos, candidatosPara, chavesDaPlanilha } from "./dados/orfaos";
import { Nota } from "./styles";

function descreverOrfao(orfao) {
  const partes = [];
  if (orfao.alocacao) partes.push("lugar no mapa");
  if (orfao.tarefas) partes.push(plural(orfao.tarefas, "tarefa", "tarefas"));
  if (orfao.pecas) partes.push(plural(orfao.pecas, "peça", "peças"));
  if (orfao.comentarios) partes.push(plural(orfao.comentarios, "comentário", "comentários"));
  if (orfao.projetista) partes.push("projetista do executivo");
  if (orfao.excecao) partes.push("carga na marcenaria");
  return partes.join(", ");
}

function LinhaOrfao({ orfao, linhas, estado, request, onReligado }) {
  const candidatos = useMemo(() => candidatosPara(orfao, linhas, estado), [orfao, linhas, estado]);
  const [para, setPara] = useState("");
  const escolhido = candidatos.find((c) => c.chave === para) || null;
  const mesmoPedido = candidatos.filter((c) => c.mesmoPedido);
  const outros = candidatos.filter((c) => !c.mesmoPedido);

  const religar = async () => {
    await religarAmbiente(request, { de: orfao.chave, para: escolhido.chave, rotulo: escolhido.rotulo });
    // Comentários, projetista e carga seguem a corrente de religações no
    // backend: basta reler para eles aparecerem no nome novo.
    recarregarContagemComentarios();
    recarregarProjetistasExecutivo();
    recarregarExcecoesCapacidade();
    onReligado();
  };

  return (
    <Item>
      <strong>{orfao.rotulo || orfao.chave}</strong>
      <Nota>{`Guardado no sistema: ${descreverOrfao(orfao)}.`}</Nota>
      <label>
        <span>Agora se chama</span>
        <select value={para} onChange={(e) => setPara(e.target.value)}>
          <option value="">Escolha o ambiente na planilha</option>
          {mesmoPedido.length > 0 && (
            <optgroup label="Mesmo pedido">
              {mesmoPedido.map((c) => <option key={c.chave} value={c.chave}>{c.rotulo}</option>)}
            </optgroup>
          )}
          <optgroup label="Outros pedidos">
            {outros.map((c) => <option key={c.chave} value={c.chave}>{c.rotulo}</option>)}
          </optgroup>
        </select>
      </label>
      <AsyncButton onClick={religar} disabled={!escolhido} successMessage="Ambiente religado.">
        Religar
      </AsyncButton>
    </Item>
  );
}

/** Órfãos do Mapa (o que a planilha não conhece mais), recalculados junto com o estado. */
export function useAmbientesOrfaos(linhas, estado) {
  const chaves = useMemo(() => chavesDaPlanilha(linhas || []), [linhas]);
  const comentarios = useContagemComentarios();
  const { atribuicoes: projetistas } = useProjetistasExecutivo();
  // Quem religa (gerir) lê as marcas mesmo sem ver a Capacidade da Fábrica.
  const { excecoes } = useExcecoesCapacidade({ permissoes: PERMS_LER_EXCECAO_PARA_RELIGAR });
  return useMemo(
    () => ambientesOrfaos(estado, chaves, comentarios, { projetistas, excecoes }),
    [estado, chaves, comentarios, projetistas, excecoes],
  );
}

/**
 * O aviso e o painel ficam separados de propósito: no tablet o aviso flutua
 * sobre o mapa, e o painel (position: fixed) não pode morar dentro dele —
 * herdaria a camada do mapa e abriria por baixo do topo da página.
 */
export function AvisoDeOrfaos({ orfaos, podeGerir, onAbrir }) {
  if (!podeGerir || orfaos.length === 0) return null;
  return (
    <Aviso role="status">
      {`${plural(orfaos.length, "ambiente do mapa sumiu", "ambientes do mapa sumiram")} da planilha `
        + "(renomeado ou removido pelo PPCP). "}
      <button type="button" onClick={onAbrir}>Religar</button>
    </Aviso>
  );
}

export default function PainelDeOrfaos({ aberto, onFechar, orfaos, linhas, estado, request, onReligado }) {
  return (
    <SlideOverPanel
      open={aberto && orfaos.length > 0}
      onClose={onFechar}
      title="Ambientes que sumiram da planilha"
      subtitle="Escolha o nome novo: posto, prazos, tarefas e peças vão junto, e a história continua."
    >
      <Lista>
        {orfaos.map((orfao) => (
          <LinhaOrfao
            key={orfao.chave}
            orfao={orfao}
            linhas={linhas}
            estado={estado}
            request={request}
            onReligado={onReligado}
          />
        ))}
      </Lista>
    </SlideOverPanel>
  );
}

const Aviso = styled.p`
  margin: 0;
  padding: 8px 12px;
  border: 1px solid var(--warning-border);
  border-radius: var(--radius-lg);
  background: var(--warning-bg);
  color: var(--warning-text);
  font-size: var(--text-md);

  button {
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    font-weight: var(--weight-semibold);
    text-decoration: underline;
    cursor: pointer;
  }
`;

const Lista = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Item = styled.li`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);

  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: var(--text-sm);
    color: var(--ink-3);
  }
  select {
    min-width: 0;
    padding: 8px;
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    background: var(--surface);
    color: var(--ink);
    font: inherit;
  }
`;
